/**
 * LE REGOLE DEL TICKET DI ASSISTENZA — una sola, copiata pari pari nel server
 * (supabase/functions/_shared/ticket.ts). Il client le usa per mostrare il
 * modulo e dire cosa manca; la funzione apri-ticket le riapplica prima di
 * salvare e spedire. Se cambia qui, cambia anche di la', lo stesso giorno: il
 * test `ticket.test.ts` confronta i due file.
 *
 * Richiesta di Luca Micheli (mail del 16/10/2026, cc Umberto), scelte di Davide
 * del 17/10 (docs/ticket-assistenza/STUDIO.md). Campi e marchi sono quelli del
 * vecchio modulo Google, tutti obbligatori.
 */

/** I marchi del vecchio modulo Google, nello stesso ordine. Daitem non c'era: e' una domanda per Luca. */
export const MARCHI_TICKET = [
  "Notifier",
  "Hikvision TVCC",
  "Hikvision altro",
  "Ajax",
  "Paradox",
  "Inim incendio",
  "Inim furto",
  "Altro",
] as const;
export type MarchioTicket = (typeof MARCHI_TICKET)[number];

/**
 * L'installatore che apre il ticket da solo, dall'app. SPENTO finche' Luca e
 * Umberto non dicono si' (domanda 2 dello studio): oggi lo aprono i dipendenti.
 * Per accenderlo: `true` qui E nel server, e si ripubblica.
 */
export const TICKET_PER_INSTALLATORI = false;

/** Chi riceve il ticket (mail di Luca). Il server lo legge da EMAIL_TICKET, se c'e'. */
export const DESTINATARI_TICKET = [
  "assistenza@datacomtecnologie.it",
  "l.biagioli@datacomtecnologie.it",
  "l.micheli@datacomtecnologie.it",
];

/** Gli account di B2Brand provano il giro: la mail arriva solo a loro, mai ai tecnici. */
export const DOMINIO_PROVA = "@b2brand.it";
const DOMINI_INTERNI = ["@datacomtecnologie.it", "@b2brand.it"];

/**
 * Gli account che NON aprono ticket anche se interni: supporto@ e' il chiosco
 * PUBBLICO del banco in sede (Chromium a tutto schermo, lo usano i clienti).
 * Da li' chiunque cercherebbe telefoni ed email dei clienti del negozio.
 */
export const ACCOUNT_SENZA_TICKET = ["supporto@datacomtecnologie.it"];

export type OrigineTicket = "dipendente" | "installatore";

/** Chi apre: il dipendente (email interna) per conto di chi chiama; l'installatore per se'; nessuno altrimenti. */
export function chiApreTicket(email: string | null | undefined, haProfilo: boolean): OrigineTicket | null {
  const e = (email ?? "").trim().toLowerCase();
  if (!e || ACCOUNT_SENZA_TICKET.includes(e)) return null;
  if (DOMINI_INTERNI.some((d) => e.endsWith(d))) return "dipendente";
  if (TICKET_PER_INSTALLATORI && haProfilo) return "installatore";
  return null;
}

export function eAccountDiProva(email: string | null | undefined): boolean {
  return (email ?? "").trim().toLowerCase().endsWith(DOMINIO_PROVA);
}

export interface CampiTicket {
  ragione_sociale: string;
  referente: string;
  telefono: string;
  email: string;
  marchio: string;
  marchio_altro?: string;
  motivo: string;
}

export type CampoTicket = keyof CampiTicket;

/** Il telefono come lo scrive la gente («055/69.67.06», «+39 392 976 9430»): restano cifre, + e spazi. */
export function pulisciTelefono(t: string): string {
  return (t ?? "")
    .replace(/[^\d+ ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Cosa manca o non va, campo per campo. Vuoto = si puo' mandare. */
export function erroriTicket(c: CampiTicket): Partial<Record<CampoTicket, string>> {
  const e: Partial<Record<CampoTicket, string>> = {};
  if (!c.ragione_sociale?.trim()) e.ragione_sociale = "Scrivi la ragione sociale.";
  if (!c.referente?.trim()) e.referente = "Scrivi chi chiama.";
  if (pulisciTelefono(c.telefono).replace(/\D/g, "").length < 6) e.telefono = "Scrivi un numero per richiamarlo.";
  if (!EMAIL.test((c.email ?? "").trim())) e.email = "Scrivi un'email valida.";
  if (!(MARCHI_TICKET as readonly string[]).includes(c.marchio)) e.marchio = "Scegli il marchio.";
  else if (c.marchio === "Altro" && !c.marchio_altro?.trim()) e.marchio_altro = "Scrivi quale marchio.";
  if ((c.motivo ?? "").trim().length < 3) e.motivo = "Scrivi il motivo della chiamata.";
  return e;
}

/** Il marchio come va scritto in mail e in elenco: «Altro» diventa quello scritto. */
export function marchioDaMostrare(marchio: string, altro?: string | null): string {
  return marchio === "Altro" && altro?.trim() ? altro.trim() : marchio;
}

/** #0007: il numero come lo si dice al telefono. */
export function numeroTicket(n: number): string {
  return `#${String(n).padStart(4, "0")}`;
}

export type StatoTicket = "da_prendere" | "preso" | "chiuso";

export function etichettaStato(s: string, presoDa?: string | null): string {
  if (s === "da_prendere") return "da prendere";
  if (s === "preso") return presoDa ? `preso da ${presoDa}` : "preso";
  if (s === "chiuso") return "chiuso";
  return s;
}
